import type { CashFlowGroupBy, CashFlowParams } from '@/features/reports/api'

import { diffDias, fimDoMesComOffset, mesesNaJanela, somarDias } from './datas'

/** Janela passada fixa: contexto suficiente para ler a tendência sem poluir. */
const DIAS_PASSADOS = 30
export const DIAS_MIN = 1
/** 10 anos — teto do relatório mensal do backend (120 meses); cobre contratos
 *  de consignado de 96 meses. */
export const DIAS_MAX = 3650
/**
 * Até aqui o gráfico é DIÁRIO; acima, MENSAL. Diário em janelas longas seria
 * ilegível (anos de contratos = milhares de pontos) e estoura o teto de 366
 * dias do relatório por dia.
 */
export const DIAS_GRANULARIDADE_DIARIA = 120

/**
 * Tetos DUROS do `GET /v1/reports/cash-flow` (report_service): acima deles o
 * backend responde 422 `report_period_too_large`, que sem tratamento derruba a
 * tela inteira na fronteira de erro — os tiles somem.
 *
 * São a fonte da verdade do clamp abaixo: NENHUMA janela montada aqui pode
 * excedê-los, venha o fim de um preset (inclusive "10 anos") ou de uma data
 * digitada à mão.
 */
export const MAX_DIAS_RELATORIO = 366
export const MAX_MESES_RELATORIO = 120

/**
 * Horizontes oferecidos — o usuário escolhe em MESES/ANOS, nunca conta dias.
 *
 * Cada opção é um clique; os valores em dias cobrem de 1 mês ao teto de 10 anos
 * (o mesmo DIAS_MAX). A chave casa com `horizon.presets.*` no i18n; a ordem é a
 * de exibição, do mais curto ao mais longo.
 */
export const HORIZONTES = [
  { dias: 30, chave: 'm1' },
  { dias: 90, chave: 'm3' },
  { dias: 180, chave: 'm6' },
  { dias: 365, chave: 'y1' },
  { dias: 730, chave: 'y2' },
  { dias: 1825, chave: 'y5' },
  { dias: DIAS_MAX, chave: 'y10' },
] as const

/**
 * Preset a destacar para um dado `dias`: o exato ou, para links antigos com um
 * valor arbitrário (o schema ainda aceita qualquer 1..3650), o mais próximo —
 * assim o controle nunca fica sem nenhuma opção acesa.
 */
export function horizonteAtivo(dias: number): number {
  let melhor: number = HORIZONTES[0].dias
  for (const h of HORIZONTES) {
    if (Math.abs(h.dias - dias) < Math.abs(melhor - dias)) melhor = h.dias
  }
  return melhor
}

/** Search da tela: horizonte em dias (preset) e/ou data final digitada à mão. */
export interface HorizonteSearch {
  readonly dias: number
  /** Data final ISO (YYYY-MM-DD). Quando presente, VENCE o preset `dias`. */
  readonly ate?: string | undefined
}

export interface Janela {
  readonly hoje: string
  readonly from: string
  readonly to: string
  readonly groupBy: CashFlowGroupBy
}

/**
 * Encolhe `to` até a janela [from, to] caber no teto do backend para a
 * granularidade escolhida. Só reduz o FIM — `from` (o passado) é intocável.
 *
 * É a rede que garante que um preset longo ou uma data absurda digitada à mão
 * nunca vire 422. Encolher em vez de rejeitar preserva a intenção ("o mais longe
 * que dá"), e o rótulo "até {data}" mostra o fim REAL, então nada mente.
 */
function limitarAoTeto(from: string, to: string, groupBy: CashFlowGroupBy): string {
  if (groupBy === 'month') {
    if (mesesNaJanela(from, to) <= MAX_MESES_RELATORIO) return to
    // Último dia do mês que fecha exatamente MAX_MESES_RELATORIO buckets a
    // partir do mês de `from` (offset = teto − 1: o mês de `from` já conta 1).
    return fimDoMesComOffset(from, MAX_MESES_RELATORIO - 1)
  }
  if (diffDias(from, to) + 1 <= MAX_DIAS_RELATORIO) return to
  return somarDias(from, MAX_DIAS_RELATORIO - 1)
}

/**
 * Resolve a janela consultada a partir do search e de `hoje`.
 *
 * O fim (`to`) é a data digitada à mão (`ate`), se houver, senão `hoje + dias`.
 * Ele é ancorado a hoje e ENCOLHIDO ao teto do backend — o request resultante é
 * sempre válido. A granularidade segue o TAMANHO da janela futura pedida, não o
 * preset, para valer igual quando o fim vem de uma data à mão.
 */
export function resolverJanela(search: HorizonteSearch, hoje: string): Janela {
  const from = somarDias(hoje, -DIAS_PASSADOS)
  // Nunca projetar para trás: um `ate` no passado deixaria from > to (422) e
  // não faz sentido — o fim é, no mínimo, hoje.
  const alvo =
    search.ate && search.ate > hoje ? search.ate : somarDias(hoje, Math.max(search.dias, DIAS_MIN))
  const futuros = diffDias(hoje, alvo)
  const groupBy: CashFlowGroupBy = futuros <= DIAS_GRANULARIDADE_DIARIA ? 'day' : 'month'
  const to = limitarAoTeto(from, alvo, groupBy)
  return { hoje, from, to, groupBy }
}

/**
 * Data final MÁXIMA que o backend aceita a partir de hoje — o teto do seletor de
 * data manual, para o usuário não conseguir pedir uma janela que seria
 * encolhida (surpresa) ou rejeitada.
 */
export function fimMaximo(hoje: string): string {
  const from = somarDias(hoje, -DIAS_PASSADOS)
  return fimDoMesComOffset(from, MAX_MESES_RELATORIO - 1)
}

/** Params do relatório da tela: projeção E origem bancária, sempre. */
export function paramsDoRelatorio(j: Janela): CashFlowParams {
  return {
    from: j.from,
    to: j.to,
    group_by: j.groupBy,
    include_projected: true,
    include_bank: true,
  }
}
